/**
 * Fix T / T2 — "Pause after this game" in the Moves tab. ONE selector for
 * `ArenaPauseButton` (fix D / R5's "one selector per leaf"): `paused` /
 * `arenaGame` / `phase`, none of which move per chess move (render budget:
 * everything but Board/Clock/MoveList is 0 per opponent move) — the move
 * count must NOT be here.
 *
 * `arenaGame` requires BOTH `tournament.mode === 'arena'` AND
 * `game.gamePlayMode === 'arena'` (not just the tournament family): the
 * board I am looking at has to be an arena GAME specifically — the same two
 * fields `reducers/game.ts`'s own `tournamentSpawnSlices`/`isTournamentMode`
 * check adopts from the spawn.
 *
 * T2's pause/resume decision (T-d, `PARITY.md`): `resume()`
 * (`cc-game-service/common/arenaHandler.js`) clears the `paused` pstate flag
 * UNCONDITIONALLY, but the actual re-pool
 * (`redisClient.arenaPoolUpsert` → `arenaPoolUpsert.lua`) is REFUSED with
 * `{0, 'live'}` while `redis:player:<uid>:arena_live` exists — i.e. while the
 * player is still mid-game. So a mid-game "Resume" tap cannot pool a player
 * who is still playing (no "paired twice" bug): it only un-arms the pending
 * pause, so the normal re-pool at the CURRENT game's end goes ahead instead
 * of leaving the player paused. `resume()` is therefore a safe TOGGLE.
 */
import type {GamePhase} from '@/Store/liveGame/state';
import type {LiveGameState} from '@/Store/liveGameStore';

export type ArenaPauseButtonInput = {
  paused: boolean;
  arenaGame: boolean;
  phase: GamePhase;
};

export const selectArenaPauseButton = (
  s: LiveGameState,
): ArenaPauseButtonInput => ({
  paused: !!s.arena.status?.paused || s.arena.status?.myState === 'paused',
  arenaGame: s.tournament.mode === 'arena' && s.game.gamePlayMode === 'arena',
  phase: s.game.phase,
});

/** LIVE face, an arena game, still playing or about to be. */
export const showArenaPauseButton = (input: ArenaPauseButtonInput): boolean =>
  input.arenaGame && (input.phase === 'playing' || input.phase === 'starting');

export default selectArenaPauseButton;
